/**
 * The door. Nothing inside the app opens without passing through it.
 *
 * Two ways in, and no password either way: Google, which is one tap for most
 * people arriving on a phone, and a link sent to an e-mail address for those
 * who have no Google account or would rather not use it here.
 *
 * The e-mail link comes back to the app on a cold start, so finishing it is
 * `EmailLinkGate`'s job — mounted above the router, because the link can land
 * on any route.
 */
import { useEffect, useRef, useState } from "react";
import { Loader2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  completeEmailLink,
  isEmailLink,
  pendingEmail,
  sendEmailLink,
  signInWithGoogle,
} from "@/lib/firebase";
import { useSession } from "@/state/session";
import { useI18n, type Translate } from "@/i18n";
import { localize, navigate } from "@/lib/router";
import { pendingMarketingConsent, setPendingMarketingConsent } from "@/lib/marketing-consent";
import { track } from "@/lib/analytics";

type Stage =
  | { kind: "form" }
  | { kind: "google" }
  | { kind: "sending" }
  | { kind: "sent"; email: string };

export function SignInPage() {
  const { t } = useI18n();
  const [stage, setStage] = useState<Stage>({ kind: "form" });
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const busy = stage.kind === "google" || stage.kind === "sending";

  // Rendered in place of a closed page, so the page view names the page that
  // was asked for; this says the door is what was actually shown.
  useEffect(() => track("sign_in_prompt"), []);

  async function google(): Promise<void> {
    setStage({ kind: "google" });
    setError(null);
    try {
      // A popup that goes through leaves this component mounted for a moment
      // longer; `onAuthStateChanged` swaps the page out from under it.
      const went = await signInWithGoogle();
      if (!went) setStage({ kind: "form" });
    } catch (err) {
      setError(messageFor(err, t));
      setStage({ kind: "form" });
    }
  }

  async function send(): Promise<void> {
    const address = email.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(address)) {
      setError(t("auth.errorInvalidEmail"));
      return;
    }
    setStage({ kind: "sending" });
    setError(null);
    try {
      await sendEmailLink(address);
      setStage({ kind: "sent", email: address });
    } catch (err) {
      setError(messageFor(err, t));
      setStage({ kind: "form" });
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-md flex-col gap-4 py-6">
      <h1 className="text-2xl font-extrabold tracking-tight">{t("auth.signInTitle")}</h1>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {stage.kind === "sent" ? (
        <Alert>
          <Mail className="size-4" />
          <AlertTitle>{t("auth.linkSentTitle")}</AlertTitle>
          <AlertDescription className="flex flex-col gap-1">
            <span>{t("auth.linkSentText", { email: stage.email })}</span>
            <span className="text-xs">{t("auth.linkSentSpam")}</span>
          </AlertDescription>
        </Alert>
      ) : (
        <Card>
          <CardContent className="flex flex-col gap-4">
            <Button size="lg" variant="outline" disabled={busy} onClick={() => void google()}>
              {stage.kind === "google" ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <GoogleMark />
              )}
              {t("auth.withGoogle")}
            </Button>

            <div className="flex items-center gap-3 text-xs uppercase tracking-wide text-muted-foreground">
              <span className="h-px flex-1 bg-border" />
              {t("auth.or")}
              <span className="h-px flex-1 bg-border" />
            </div>

            <form
              noValidate
              className="flex flex-col gap-3"
              onSubmit={(e) => {
                e.preventDefault();
                void send();
              }}
            >
              <div>
                <Label htmlFor="email" className="mb-1.5 text-xs">
                  {t("auth.emailLabel")}
                </Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  placeholder={t("auth.emailPlaceholder")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <Button type="submit" disabled={busy}>
                {stage.kind === "sending" ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Mail className="size-4" />
                )}
                {stage.kind === "sending" ? t("auth.sending") : t("auth.sendLink")}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {stage.kind !== "sent" && <Consent />}
    </div>
  );
}

/**
 * What signing in agrees to, and the one thing it does not.
 *
 * Accepting the terms is what an account is; the notice says so and links both
 * documents. Mailings beyond the account's own are a separate, unticked box:
 * consent folded into accepting the terms is not consent (RODO art. 7(4)), and
 * in Poland an e-mail campaign needs it (Prawo komunikacji elektronicznej
 * art. 398). The box is remembered until `/session` delivers it — see
 * `marketing-consent.ts`.
 */
function Consent() {
  const { t, messages, locale } = useI18n();
  const [ticked, setTicked] = useState(pendingMarketingConsent);

  const doc = (path: string, label: string) => (
    // A new tab, so the form and the box stay as they were.
    <a
      key={path}
      href={localize(path, locale)}
      target="_blank"
      rel="noopener"
      className="underline underline-offset-2 hover:text-foreground"
    >
      {label}
    </a>
  );
  const notice = messages.auth.consentNotice
    .split(/(\{terms\}|\{privacy\})/)
    .map((part) =>
      part === "{terms}"
        ? doc("/terms", t("auth.consentTerms"))
        : part === "{privacy}"
          ? doc("/privacy", t("auth.consentPrivacy"))
          : part,
    );

  return (
    <div className="flex flex-col gap-3 px-1 text-xs leading-relaxed text-muted-foreground">
      <label className="flex cursor-pointer items-start gap-2.5">
        <input
          type="checkbox"
          className="mt-0.5 size-4 shrink-0 accent-primary"
          checked={ticked}
          onChange={(e) => {
            setTicked(e.target.checked);
            setPendingMarketingConsent(e.target.checked);
          }}
        />
        <span>{t("auth.marketingOptIn")}</span>
      </label>
      <p>{notice}</p>
    </div>
  );
}

/**
 * Finishing the e-mail link.
 *
 * The link returns to the site root on a cold start, which is a public page,
 * so this sits above the router rather than on any one route: while it is
 * working it owns the screen, and when it is done the app carries on where a
 * signed-in visitor belongs.
 *
 * Returns `null` when there is no link to redeem — the overwhelmingly common
 * case — and the app renders as usual.
 */
export function EmailLinkGate({ children }: { children: React.ReactNode }) {
  const { t } = useI18n();
  const session = useSession();
  const [active] = useState(() => isEmailLink(window.location.href));
  const [asking, setAsking] = useState(false);
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // A sign-in link is single-use, so this must happen exactly once — under
  // StrictMode the effect runs twice, and the second attempt came back
  // "link expired or already used" over a sign-in that had just worked.
  const redeeming = useRef(false);
  useEffect(() => {
    if (!active || redeeming.current) return;
    redeeming.current = true;
    const known = pendingEmail();
    if (known) void finish(known);
    else setAsking(true);
    // `finish` closes over nothing that changes between renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  async function finish(address: string): Promise<void> {
    setAsking(false);
    setError(null);
    try {
      await completeEmailLink(address, window.location.href);
      await session.refresh();
      // Drop the link parameters so a refresh does not try to sign in twice.
      setDone(true);
      navigate("/app", true);
    } catch (err) {
      setError(messageFor(err, t));
      setAsking(true);
    }
  }

  if (!active || done) return <>{children}</>;

  return (
    <div className="mx-auto flex w-full max-w-md flex-col gap-4 py-6">
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {asking ? (
        <Card>
          <CardHeader className="gap-1.5">
            <CardTitle className="text-base">{t("auth.confirmEmailTitle")}</CardTitle>
            <p className="text-sm leading-relaxed text-muted-foreground">
              {t("auth.confirmEmailText")}
            </p>
          </CardHeader>
          <CardContent>
            <form
              noValidate
              className="flex flex-col gap-3 sm:flex-row sm:items-end"
              onSubmit={(e) => {
                e.preventDefault();
                void finish(email.trim());
              }}
            >
              <div className="flex-1">
                <Label htmlFor="confirm-email" className="mb-1.5 text-xs">
                  {t("auth.emailLabel")}
                </Label>
                <Input
                  id="confirm-email"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  placeholder={t("auth.emailPlaceholder")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <Button type="submit">{t("auth.confirm")}</Button>
            </form>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="flex items-center gap-3 py-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            {t("auth.completing")}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/** Google's mark, inline: the CDN it is normally served from is one more thing to fail. */
function GoogleMark() {
  return (
    <svg className="size-4" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

/** Firebase error codes we can say something useful about. */
export function messageFor(err: unknown, t: Translate): string {
  switch ((err as { code?: string }).code) {
    case "auth/invalid-email":
      return t("auth.errorInvalidEmail");
    case "auth/invalid-action-code":
    case "auth/expired-action-code":
      return t("auth.errorLinkExpired");
    case "auth/account-exists-with-different-credential":
      return t("auth.errorOtherProvider");
    case "auth/network-request-failed":
      return t("auth.errorNetwork");
    default: {
      // A code nobody mapped is the one that needs reporting, so it is shown.
      const code = (err as { code?: string }).code;
      return code ? `${t("auth.errorGeneric")} (${code})` : t("auth.errorGeneric");
    }
  }
}
